import { useEffect, useState } from "react";
import { api } from "@/lib/api-client";
import { showApiError } from "@/lib/error-toast";
import { useCurrentTenant } from "@/hooks/useCurrentTenant";
import styles from "./tenant-settings.module.css";

/**
 * R-155 — tenant-level toggle for the auto-export gate.
 *
 * When OFF (the default), batches whose job type has an active export
 * destination wired auto-fire the export as soon as all documents are
 * keyed and validated. When ON, every batch waits for a supervisor to
 * click "Release for Export."
 *
 * Per-job-type overrides live on JobType.requireManualExportRelease and
 * are configured from the Job Type → General tab.
 */
export function AutoExportSection() {
  const { data: tenant, loading } = useCurrentTenant();
  const [manualRequired, setManualRequired] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    if (!tenant) return;
    setManualRequired(tenant.requireManualExportRelease ?? false);
  }, [tenant]);

  const handleToggle = async (next: boolean) => {
    // Optimistic update so the toggle responds instantly; we revert on
    // failure rather than blocking the UI on the round-trip.
    setManualRequired(next);
    setSaving(true);
    setMessage(null);
    try {
      await api.patch("/me/tenant", { requireManualExportRelease: next });
      setMessage({
        kind: "ok",
        text: next ? "Manual release required for all batches" : "Auto-release enabled (default)",
      });
    } catch (e) {
      setManualRequired(!next);
      showApiError(e, { fallback: "Failed to save auto-export setting" });
      setMessage({ kind: "err", text: "Save failed — toggle reverted." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionHeading}>Auto-export release</h2>
      <p className={styles.overviewHint}>
        Controls whether keyed + validated batches fire their export
        automatically, or wait for an operator to click "Release for Export."
        Individual job types can override this default from their General tab.
      </p>
      <label
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "var(--space-3, 0.75rem)",
          marginTop: "var(--space-4, 1rem)",
          cursor: loading || saving ? "wait" : "pointer",
        }}
      >
        <input
          type="checkbox"
          checked={manualRequired}
          disabled={loading || saving}
          onChange={(e) => handleToggle(e.target.checked)}
          data-testid="tenant-manual-release-toggle"
          style={{ marginTop: "2px" }}
        />
        <span>
          <strong>Hold each ready export bundle for supervisor release</strong>
          <span className={styles.overviewHint} style={{ display: "block", marginTop: "2px" }}>
            {manualRequired
              ? "All batches wait for a supervisor click."
              : "Batches with active destinations fire automatically."}
            <span style={{ display: "block", marginTop: "2px" }}>
              When on, accumulated bundles (and batch releases) wait on the Pending Release page until a supervisor approves them.
            </span>
          </span>
        </span>
      </label>
      {message && (
        <div
          className={message.kind === "ok" ? styles.testOk : styles.testErr}
          role={message.kind === "err" ? "alert" : "status"}
          style={{ marginTop: "var(--space-3, 0.75rem)" }}
        >
          {message.text}
        </div>
      )}
    </section>
  );
}
